import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import SubstanceProperty from '@Components/Results/substances/SubstanceProperty';
import { getFactCount } from '@Components/Results/substances/utils/factCountHelper';
import { COMMERCIAL_SUPPLIERS_FACT_ID } from '@Constants';
import NumberFormatter from '@Utils/NumberFormatter';
import Slugify from '@Utils/Slugify';

function getCardHeading(fact, substance) {
  const title = fact.title || fact.id || 'Substance data';
  const count = getFactCount(fact, substance);
  return count ? `${title} - ${NumberFormatter(count, 0)}` : title;
}

function SubstanceReaxysInformation({ metadata, substance, details, database, substanceIndex, facts, zoom, onCloseFact }) {
  const openFactEntries = useMemo(() => Object.entries(facts || {}).filter(([, fact]) => !fact.hitData && fact.open), [facts]);

  if (!openFactEntries.length) return null;

  return (
    <div id={`substance-fact-details-${substanceIndex}`} className="substance-fact-details">
      {openFactEntries.map(([id, fact]) => {
        if (fact.id === COMMERCIAL_SUPPLIERS_FACT_ID) {
          return (
            <SubstanceProperty
              key={id}
              metadata={metadata}
              substance={substance}
              fact={fact}
              details={details}
              database={database}
              substanceIndex={substanceIndex}
              zoom={zoom}
              isTabPanel
            />
          );
        }

        const heading = getCardHeading(fact, substance);
        const factId = fact.id || id || fact.title || 'substance-data';
        const headingId = `substance-fact-card-heading-${Slugify(factId)}-${substanceIndex}`;

        return (
          <Card
            key={id}
            className="substance-fact-details-card"
            heading={
              <div className="substance-fact-details-card__header">
                <h3 id={headingId} className="substance-fact-details-card__title">
                  {heading}
                </h3>
                <ButtonWithIcon
                  data-e2e="substance-fact-details-close"
                  type="borderless"
                  size="x-small"
                  sprite="Close"
                  iconRight
                  iconOnly
                  className="substance-fact-details-card__close"
                  aria-label={`Close ${heading}`}
                  onClick={() => onCloseFact(fact)}>
                  Close {heading}
                </ButtonWithIcon>
              </div>
            }
            headingLevel="h3"
            id={`substance-fact-card-${Slugify(factId)}-${substanceIndex}`}
            // RX-23573: no `role="region"` — per-result panel landmark overuse (ARIA APG). `<h3>` heading retained.
            aria-labelledby={headingId}>
            <SubstanceProperty
              metadata={metadata}
              substance={substance}
              fact={fact}
              details={details}
              database={database}
              substanceIndex={substanceIndex}
              zoom={zoom}
              isTabPanel
            />
          </Card>
        );
      })}
    </div>
  );
}

SubstanceReaxysInformation.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  details: PropTypes.object.isRequired,
  database: PropTypes.object.isRequired,
  substanceIndex: PropTypes.string.isRequired,
  facts: PropTypes.object,
  zoom: PropTypes.number,
  onCloseFact: PropTypes.func.isRequired,
};

export default SubstanceReaxysInformation;
